import { ChannelType } from "store/types";

/** Article rights base since the press revamp — stored on articles.calculationBase. */
export type ArticleCalculationBase = "folio" | "amount";

/**
 * Stable press channel slot from the DB `channel.name` before i18n rename in useFormatChannels.
 * Also set as `pressSlot` on formatted channels so UI can keep matching after rename.
 */
export type PressChannelSlot = "folio" | "amount" | "minimum_guaranteed";

export const isArticleCalculationBase = (value: unknown): value is ArticleCalculationBase =>
    value === "folio" || value === "amount";

/**
 * Resolves the press channel slot from raw or formatted channel data.
 * Prefer `pressSlot` (set by useFormatChannels), then raw `name`, then legacy `type`.
 */
type PressChannelLike = {
    name?: string | null;
    type?: number | null;
    pressSlot?: PressChannelSlot | null;
};

export const resolvePressChannelSlot = (channel: PressChannelLike): PressChannelSlot | null => {
    if (channel.pressSlot === "folio" || channel.pressSlot === "amount" || channel.pressSlot === "minimum_guaranteed") {
        return channel.pressSlot;
    }

    if (channel.name === "folio" || channel.name === "amount" || channel.name === "minimum_guaranteed") {
        return channel.name;
    }

    if (channel.type === ChannelType.FOLIO_BASED) return "folio";
    if (channel.type === ChannelType.AMOUNT_BASED) {
        return channel.name?.toLowerCase().includes("minimum") ? "minimum_guaranteed" : "amount";
    }

    return null;
};

export const articleCalculationBaseFromSlot = (slot: PressChannelSlot | null | undefined): ArticleCalculationBase =>
    slot === "folio" ? "folio" : "amount";

/**
 * Prefer the article's persisted calculationBase; fall back to the linked channel slot / legacy type.
 */
export const resolveArticleCalculationBase = ({
    calculationBase,
    channel,
}: {
    calculationBase?: ArticleCalculationBase | null;
    channel?: PressChannelLike | null;
}): ArticleCalculationBase => {
    if (isArticleCalculationBase(calculationBase)) return calculationBase;
    return articleCalculationBaseFromSlot(channel ? resolvePressChannelSlot(channel) : null);
};
